/**
 * Alpine x-data factory for poll create/edit builder.
 * Must be on window before Alpine.start() (same pattern as surveyResponseForm).
 */
export function surveyBuilder(initialQuestions, uploadUrl) {
    let choiceIdCounter = 0;

    const nextChoiceId = () => ++choiceIdCounter;

    const defaultChoices = () => [
        { _id: nextChoiceId(), label: '', value: '', image_url: '' },
        { _id: nextChoiceId(), label: '', value: '', image_url: '' },
    ];

    const defaultQuestion = () => ({
        type: 'radio',
        label: '',
        help_text: '',
        is_required: false,
        conditional: { enabled: false, depends_on_index: 0, operator: 'equals', value: '' },
        options: {
            choices: defaultChoices(),
            min: 1,
            max: 5,
            min_label: '',
            max_label: '',
        },
    });

    const isChoiceType = (type) => ['radio', 'checkbox', 'select', 'image_choice'].includes(type);

    const normalizeChoice = (choice = {}) => ({
        _id: nextChoiceId(),
        label: choice.label ?? '',
        value: choice.value ?? '',
        image_url: choice.image_url ?? '',
    });

    const normalize = (q) => {
        const base = defaultQuestion();
        const type = q.type ?? base.type;
        const rawChoices = q.options?.choices;
        const choices = Array.isArray(rawChoices) && rawChoices.length
            ? rawChoices.map(normalizeChoice)
            : defaultChoices();

        return {
            type,
            label: q.label ?? '',
            help_text: q.help_text ?? '',
            is_required: Boolean(q.is_required),
            conditional: { ...base.conditional, ...(q.conditional || {}) },
            options: {
                min: q.options?.min ?? base.options.min,
                max: q.options?.max ?? base.options.max,
                min_label: q.options?.min_label ?? base.options.min_label,
                max_label: q.options?.max_label ?? base.options.max_label,
                choices: isChoiceType(type) ? choices : [],
            },
        };
    };

    return {
        uploadUrl,
        questions: (initialQuestions && initialQuestions.length)
            ? initialQuestions.map(normalize)
            : [defaultQuestion()],
        isChoiceType,
        choiceImageSrc(url) {
            if (!url) {
                return '';
            }
            if (/^https?:\/\//i.test(url)) {
                return url;
            }
            if (url.startsWith('/storage/')) {
                return url;
            }
            if (url.startsWith('/')) {
                return url;
            }

            return '/storage/' + String(url).replace(/^\/+/, '');
        },
        addQuestion() {
            this.questions.push(defaultQuestion());
        },
        removeQuestion(index) {
            this.questions.splice(index, 1);
        },
        moveUp(index) {
            if (index === 0) {
                return;
            }
            const item = this.questions.splice(index, 1)[0];
            this.questions.splice(index - 1, 0, item);
        },
        moveDown(index) {
            if (index >= this.questions.length - 1) {
                return;
            }
            const item = this.questions.splice(index, 1)[0];
            this.questions.splice(index + 1, 0, item);
        },
        addChoice(question) {
            question.options.choices.push({
                _id: nextChoiceId(),
                label: '',
                value: '',
                image_url: '',
            });
        },
        onTypeChange(question) {
            if (this.isChoiceType(question.type) && (!question.options.choices || !question.options.choices.length)) {
                question.options.choices = defaultChoices();
            }
            if (question.type === 'nps') {
                question.options.min = 0;
                question.options.max = 10;
            }
        },
        async uploadChoiceImage(event, question, choiceIndex) {
            const file = event.target.files?.[0];
            if (!file || !this.uploadUrl) {
                return;
            }
            const formData = new FormData();
            formData.append('image', file);
            formData.append('_token', document.querySelector('meta[name="csrf-token"]')?.content || '');
            try {
                const res = await fetch(this.uploadUrl, { method: 'POST', body: formData, credentials: 'same-origin' });
                const data = await res.json();
                if (data.success) {
                    const path = data.path || data.url || '';
                    if (question.options.choices[choiceIndex]) {
                        question.options.choices[choiceIndex].image_url = path;
                    }
                    // Prevent stale file inputs from re-uploading the wrong choice on save.
                    event.target.value = '';
                }
            } catch (e) {
                console.error('Image upload failed', e);
            }
        },
    };
}
